// src/pages/JobsPage.js
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../../styles/JobsPage/jobs.css';
import { Search, MapPin, Briefcase } from 'lucide-react';

const Jobs = ({ jobs, searchTerm, locationTerm, selectedFilters }) => {
  // Filter and sort jobs based on props
  const [filteredJobs, setFilteredJobs] = useState([]);

  useEffect(() => {
    let results = jobs.filter(job =>
      (job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
       job.company.toLowerCase().includes(searchTerm.toLowerCase())) &&
      job.location.toLowerCase().includes(locationTerm.toLowerCase())
    );

    if (selectedFilters.experience > 0) {
      results = results.filter(job => job.experience <= selectedFilters.experience);
    }

    if (selectedFilters.jobType !== 'All') {
      results = results.filter(job => job.jobType === selectedFilters.jobType);
    }

    if (selectedFilters.industry !== 'All') {
      results = results.filter(job => job.industry === selectedFilters.industry);
    }

    if (selectedFilters.sortBy === 'Date Posted') {
      results.sort((a, b) => new Date(b.datePosted) - new Date(a.datePosted));
    } // else relevance (original order)

    setFilteredJobs(results);
  }, [jobs, searchTerm, locationTerm, selectedFilters]);

  return (
    <div className="jobs-wrapper">
      <div className="jobs-content">
        <div className="sidebar">
          <h3>Sort & Filter</h3>

          <div className="filter-section">
            <label>Sort By</label>
            <select
              value={selectedFilters.sortBy}
              onChange={(e) => selectedFilters.setSortBy(e.target.value)}
            >
              <option>Relevance</option>
              <option>Date Posted</option>
            </select>
          </div>

          <div className="filter-section">
            <label>Job Type</label>
            <select
              value={selectedFilters.jobType}
              onChange={(e) => selectedFilters.setJobType(e.target.value)}
            >
              <option>All</option>
              <option>Full-Time</option>
              <option>Part-Time</option>
              <option>Internship</option>
            </select>
          </div>

          <div className="filter-section">
            <label>Industry</label>
            <select
              value={selectedFilters.industry}
              onChange={(e) => selectedFilters.setIndustry(e.target.value)}
            >
              <option>All</option>
              <option>IT</option>
              <option>Finance</option>
              <option>Education</option>
            </select>
          </div>

          <div className="filter-section">
            <label>Experience</label>
            <input 
              type="range" 
              min="0" 
              max="10" 
              value={selectedFilters.experience} 
              onChange={(e) => selectedFilters.setExperience(parseInt(e.target.value))} 
              className="experience-slider" 
            />
            <div className="exp-values">
              <span>0 Yrs</span>
              <span>{selectedFilters.experience} Yrs</span>
            </div>
          </div>
        </div>

        <div className="job-list">
          {filteredJobs.length > 0 ? (
            filteredJobs.map((job) => (
              <Link to={`/jobs/${job._id}`} key={job._id} className="job-link">
                <div className="job-item">
                  <div className="job-header">
                    <div className="job-info">
                      <h3 className="job-title">{job.title}</h3>
                      <p className="job-company">{job.company}</p>
                      <div className="job-meta">
                        <div className="meta-group"><Briefcase className="meta-icon" /> {job.experience} Yrs</div>
                        <div className="meta-group"><MapPin className="meta-icon" /> {job.location}</div>
                      </div>
                    </div>
                    {job.logo && <img src={job.logo} alt={job.company} className="job-logo" />}
                  </div>
                  <hr className="divider" />
                  <p className="job-description">{job.shortdesc}</p>
                  <div className="skills">
                    {job.skills?.map((skill, index) => (
                      <span className="skill-tag" key={index}>{skill}</span>
                    ))}
                  </div>
                </div>
              </Link>
            ))
          ) : (
            <p className="no-results">No jobs found</p>
          )}
        </div>
      </div>
    </div>
  );
};

const JobsPage = () => {
  const [jobs, setJobs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationTerm, setLocationTerm] = useState('');
  const [experience, setExperience] = useState(0);
  const [jobType, setJobType] = useState('All');
  const [industry, setIndustry] = useState('All');
  const [sortBy, setSortBy] = useState('Relevance');
  const jobsRef = useRef(null);

  const handleSearch = () => {
    if (jobsRef.current) {
      jobsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Fetch jobs from API on mount
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await fetch('http://localhost:3000/api/jobs');
        if (!response.ok) throw new Error('Failed to fetch jobs');
        const data = await response.json();
        setJobs(data);  // assuming the API returns an array of job objects
      } catch (error) {
        console.error('Error fetching jobs:', error);
        alert('Error fetching jobs: ' + error.message);
      }
    };

    fetchJobs();
  }, []);

  const selectedFilters = { 
    experience, setExperience, 
    jobType, setJobType, 
    industry, setIndustry,
    sortBy, setSortBy
  };

  return (
    <div className="jobs-page">
      <div className="hero-section">
        <div className="hero-overlay">
          <h1 className="hero-title">Find Your Dream Job</h1>
          <p className="hero-subtitle">
            New opportunities to explore every day - find your perfect fit!
          </p>
          <div className="hero-search-bar">
            <div className="input-container">
              <Search className="hero-search-icon" />
              <input 
                type="text" 
                placeholder="Job Title or Keywords" 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
              />
            </div>

            <div className="input-container">
              <MapPin className="hero-location-icon" />
              <input 
                type="text" 
                placeholder="Location" 
                value={locationTerm} 
                onChange={(e) => setLocationTerm(e.target.value)} 
              />
            </div>

            <button onClick={handleSearch}>Search</button>
          </div>
        </div>
      </div>

      <div ref={jobsRef}>
        <Jobs 
          jobs={jobs}
          searchTerm={searchTerm} 
          locationTerm={locationTerm} 
          selectedFilters={selectedFilters} 
        />
      </div>
    </div>
  );
};

export default JobsPage;
